import { HeroContainer } from '@seek/brand-elements';
import { type Market, MarketSelector } from '@ts-internal/shared-lib';
import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Button,
  IconFilter,
  IconSearch,
  Hidden,
  TextField,
} from 'braid-design-system';
import type { FormEventHandler } from 'react';

import translations from './.vocab';
import userImageAU from './images/desktop/people-search-desktop-au.webp';
import userImageHK from './images/desktop/people-search-desktop-hk.webp';
import userImageID from './images/desktop/people-search-desktop-id.webp';
import userImageMY from './images/desktop/people-search-desktop-my.webp';
import userImageNZ from './images/desktop/people-search-desktop-nz.webp';
import userImagePH from './images/desktop/people-search-desktop-ph.webp';
import userImageSG from './images/desktop/people-search-desktop-sg.webp';
import userImageTH from './images/desktop/people-search-desktop-th.webp';

import * as styles from './SearchBanner.css';

interface Props {
  searchQuery: {
    value: string;
    onChange: FormEventHandler<HTMLInputElement>;
    onClear: () => void;
    valid: boolean;
    errorMessage?: string;
  };
  market: {
    value: Market;
    onChange: (marketSelected: Market) => void;
  };
  domainMarket: Market;
  filterItemsCount: number;
  showFilterButton: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onFiltersButtonClick: () => void;
}

const getImageFileName = (market: Market): string => {
  switch (market) {
    case 'ID': {
      return userImageID;
    }
    case 'PH': {
      return userImagePH;
    }
    case 'HK': {
      return userImageHK;
    }
    case 'MY': {
      return userImageMY;
    }
    case 'NZ': {
      return userImageNZ;
    }
    case 'SG': {
      return userImageSG;
    }
    case 'TH': {
      return userImageTH;
    }
    case 'AU':
    default: {
      return userImageAU;
    }
  }
};

export const SearchBanner = ({
  searchQuery: {
    value: searchQuery,
    onChange: onSearchQueryChange,
    onClear: onSearchQueryClear,
    valid,
    errorMessage,
  },
  market: { value: market, onChange: onMarketChange },
  domainMarket,
  filterItemsCount,
  showFilterButton,
  onSubmit,
  onFiltersButtonClick,
}: Props) => {
  const { t } = useTranslations(translations);

  const desktopHeroImage = getImageFileName(domainMarket);

  const getFilterItemsCount = () =>
    filterItemsCount > 0 ? ` (${filterItemsCount})` : '';

  return (
    <HeroContainer
      variant="photo"
      imageUrl={{
        desktop: desktopHeroImage,
        mobile: styles.mobileHeroImage,
      }}
      badge={<Badge tone="positive">{t('EARLY_RELEASE')}</Badge>}
      title={t('FIND_THE_RIGHT_PEOPLE_ON_SEEK')}
      subtitle={t('SEARCH_FOR_PEOPLE_WHO_HAVE_MADE_THEIR_PROFILES_PUBLIC')}
    >
      <form onSubmit={onSubmit} className={styles.formContainer}>
        <Box className={styles.searchField}>
          <TextField
            id="searchInput"
            label=""
            aria-label="search-input"
            value={searchQuery}
            icon={<IconSearch />}
            placeholder={t('SEARCH_NAMES')}
            tone={valid ? 'neutral' : 'critical'}
            message={errorMessage}
            onClear={onSearchQueryClear}
            onChange={onSearchQueryChange}
          />
        </Box>

        <Box className={styles.marketSelector}>
          <MarketSelector
            marketSelected={market}
            onMarketChange={onMarketChange}
          />
        </Box>

        <Box className={styles.searchButton}>
          <Button tone="brandAccent" variant="solid" type="submit">
            {t('SEEK')}
          </Button>
        </Box>

        {showFilterButton ? (
          <Hidden above="mobile">
            <Box className={styles.filterButton}>
              <Button
                icon={<IconFilter />}
                variant="ghost"
                onClick={onFiltersButtonClick}
              >
                {t('FILTERS')}
                {getFilterItemsCount()}
              </Button>
            </Box>
          </Hidden>
        ) : null}
      </form>
    </HeroContainer>
  );
};
